import { useEffect, useMemo, useRef, useState } from 'react';

import { Boton } from '../componentes/Boton.js';
import { Hoja, estilosHoja } from '../componentes/Hoja.js';
import { Aviso, BarraAccion, Cabecera, Contenido, Pantalla } from '../componentes/estructura.js';
import {
    etiquetaPunto,
    leerLimpieza,
    nombreEstancia,
    type EstanciaLocal,
    type LimpiezaLocal,
} from '../datos/db.js';
import { anotarIncidencia, marcarEstancia } from '../datos/limpiezas.js';
import { useGps } from '../captura/useGps.js';
import { useApp } from '../estado/app.js';
import css from './comun.module.css';

/**
 * 06 · Fotos por checklist.
 *
 * El modo alternativo al vídeo, y el plan B cuando un móvil no puede grabar:
 * cuatro fotos por estancia son una prueba más débil que un clip continuo,
 * pero son una prueba, y funcionan en cualquier teléfono.
 *
 * Se usa `<input type="file" capture>` en lugar de MediaRecorder: abre la
 * cámara nativa, que en un móvil modesto enfoca y expone mucho mejor que
 * cualquier cosa que se pueda hacer desde el navegador, y devuelve un JPEG ya
 * comprimido por el sistema. Para una foto fija no hay razón para no usarla.
 */
export function PantallaFotos({ limpiezaId, estanciaId }: { limpiezaId: string; estanciaId: string }) {
    const { t, idioma, cola, atras } = useApp();
    const { pedir } = useGps();
    const [l, setL] = useState<LimpiezaLocal | null>(null);
    const [hechas, setHechas] = useState<Record<string, string>>({});
    const [incidencia, setIncidencia] = useState(false);
    const [texto, setTexto] = useState('');
    const [gravedad, setGravedad] = useState<'baja' | 'media' | 'alta'>('media');
    const [guardada, setGuardada] = useState(false);
    const entrada = useRef<HTMLInputElement>(null);
    const puntoActual = useRef<string | null>(null);

    useEffect(() => {
        void leerLimpieza(limpiezaId).then(setL);
    }, [limpiezaId]);

    // Las URL de las miniaturas hay que soltarlas: cada una retiene el blob
    // entero en memoria, y con doce fotos de 3 MB eso es un problema real en
    // un móvil de gama baja.
    useEffect(() => {
        return () => {
            for (const url of Object.values(hechas)) {
                URL.revokeObjectURL(url);
            }
        };
    }, [hechas]);

    const estancia: EstanciaLocal | undefined = l?.estancias.find((e) => e.id === estanciaId);

    const puntos = useMemo(() => {
        if (estancia === undefined) {
            return [] as { id: string; etiqueta: string }[];
        }
        if (estancia.puntos.length > 0) {
            return estancia.puntos.map((p) => ({
                id: p.id,
                etiqueta: etiquetaPunto(p, idioma),
            }));
        }

        // Sin checklist configurado, se piden `min_fotos` sueltas. Es lo que
        // pasa en un apartamento nuevo que oficina no ha terminado de dar de
        // alta, y no puede ser un motivo para no poder trabajar.
        return Array.from({ length: estancia.min_fotos }, (_, i) => ({
            id: `libre-${i}`,
            etiqueta: `${i + 1}`,
        }));
    }, [estancia, idioma]);

    async function guardarFoto(fichero: File): Promise<void> {
        const punto = puntoActual.current;
        if (l === null || estancia === undefined || punto === null) {
            return;
        }

        const gps = await pedir();

        // El tamaño de la foto, que hasta ahora iba a `null` como el del
        // vídeo. Una foto de 200×150 no es prueba de nada, y sin estas dos
        // columnas eso solo se ve abriéndola una por una.
        //
        // En try/catch y degradando a `null` a propósito: aquí el fichero lo
        // da el sistema —`<input capture>`— y un iPhone puede entregar un
        // formato que este navegador no sepa decodificar. Que no se pueda
        // medir la foto no puede impedir que la foto se suba.
        let medida: { width: number; height: number } | null = null;
        try {
            const bmp = await createImageBitmap(fichero);
            medida = { width: bmp.width, height: bmp.height };
            bmp.close();
        } catch {
            medida = null;
        }

        await cola.anadir({
            cleaning_id: l.id,
            cleaning_room_id: estancia.id,
            room_punto_id: punto.startsWith('libre-') ? null : punto,
            apartamento: l.apartamento,
            estancia: nombreEstancia(estancia, idioma),
            tipo: 'foto',
            blob: fichero,
            capturado_at: new Date().toISOString(),
            duracion_ms: null,
            ancho: medida?.width ?? null,
            alto: medida?.height ?? null,
            calidad: null,
            gps,
        });

        setHechas((h) => ({ ...h, [punto]: URL.createObjectURL(fichero) }));
    }

    if (l === null || estancia === undefined) {
        return (
            <Pantalla>
                <Cabecera titulo={t('comun.cargando')} alVolver={atras} />
            </Pantalla>
        );
    }

    const nombre = nombreEstancia(estancia, idioma);
    const cuantas = Object.keys(hechas).length;
    const faltan = Math.max(0, estancia.min_fotos - cuantas);

    return (
        <Pantalla>
            <Cabecera titulo={t('fotos.titulo', { estancia: nombre })} alVolver={atras} />

            <Contenido>
                <p className={css.parrafo}>{t('fotos.instruccion', { n: estancia.min_fotos })}</p>

                <div className={css.rejilla}>
                    {puntos.map((p) => {
                        const url = hechas[p.id];

                        return (
                            <div key={p.id} className={css.celda}>
                                <button
                                    className={`${css.celdaFoto} ${url !== undefined ? css.celdaHecha : ''}`}
                                    onClick={() => {
                                        puntoActual.current = p.id;
                                        entrada.current?.click();
                                    }}
                                    aria-label={url !== undefined ? t('fotos.rehacer') : t('fotos.hacer')}
                                >
                                    {url !== undefined ? (
                                        <>
                                            <img className={css.celdaImagen} src={url} alt="" />
                                            <span className={css.insignia}>✓</span>
                                        </>
                                    ) : (
                                        '＋'
                                    )}
                                </button>
                                <span className={css.celdaEtiqueta}>{p.etiqueta}</span>
                            </div>
                        );
                    })}
                </div>

                {/* `capture="environment"` abre la cámara trasera directamente.
                    En iOS, sin `accept`, ofrece también la galería, y una foto
                    de la galería no prueba nada: por eso va restringido. */}
                <input
                    ref={entrada}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f !== undefined) {
                            void guardarFoto(f);
                        }
                        e.target.value = '';
                    }}
                />

                <Aviso>{t('fotos.aviso_incidencia')}</Aviso>
                {guardada && <Aviso tono="ok">{t('incidencia.guardada')}</Aviso>}

                <Boton variante="secundario" onClick={() => setIncidencia(true)}>
                    {t('fotos.anadir_incidencia')}
                </Boton>
            </Contenido>

            <BarraAccion nota={faltan > 0 ? t('fotos.faltan', { n: faltan }) : undefined}>
                <Boton
                    disabled={faltan > 0}
                    onClick={() => {
                        void (async () => {
                            await marcarEstancia(l.id, estancia.id, { estado: 'hecha', fotos: cuantas });
                            // Igual que en la pantalla de grabar: esto es un
                            // paso encima del recorrido, no otro destino.
                            atras();
                        })();
                    }}
                >
                    {t('fotos.siguiente_estancia')}
                </Boton>
            </BarraAccion>

            {incidencia && (
                <Hoja
                    titulo={t('incidencia.titulo')}
                    alCerrar={() => {
                        setIncidencia(false);
                        setTexto('');
                    }}
                >
                    <textarea
                        className={estilosHoja.campo}
                        placeholder={t('incidencia.texto_ejemplo')}
                        value={texto}
                        maxLength={800}
                        onChange={(e) => setTexto(e.target.value)}
                    />
                    <div>
                        <span className="etiqueta-mono">{t('incidencia.gravedad')}</span>
                        <div className={estilosHoja.opciones} style={{ marginTop: 'var(--e8)' }}>
                            {(['baja', 'media', 'alta'] as const).map((g) => (
                                <button
                                    key={g}
                                    className={`${estilosHoja.opcion} ${gravedad === g ? estilosHoja.opcionActiva : ''}`}
                                    onClick={() => setGravedad(g)}
                                >
                                    {t(
                                        g === 'baja'
                                            ? 'incidencia.gravedad_baja'
                                            : g === 'media'
                                              ? 'incidencia.gravedad_media'
                                              : 'incidencia.gravedad_alta'
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Boton
                        disabled={texto.trim().length < 3}
                        onClick={() => {
                            void (async () => {
                                await anotarIncidencia({
                                    cleaning_id: l.id,
                                    cleaning_room_id: estancia.id,
                                    texto: texto.trim(),
                                    gravedad,
                                });
                                setIncidencia(false);
                                setTexto('');
                                setGuardada(true);
                                void cola.arrancar('incidencia nueva');
                            })();
                        }}
                    >
                        {t('incidencia.guardar')}
                    </Boton>
                </Hoja>
            )}
        </Pantalla>
    );
}
